"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AddressAutocomplete = void 0;
var _icons = require("@ori-ui/icons");
var _mui = require("@ori-ui/mui");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _downshift = _interopRequireDefault(require("downshift"));
var _react = _interopRequireWildcard(require("react"));
var _reactHookForm = require("react-hook-form");
var _Localization = require("../../Localization");
var _constants = require("../../constants");
var _contexts = require("../../contexts");
var _hooks = require("../../hooks");
var _interfaces = require("../../interfaces");
var _models = require("../../models");
var _SharedStyledComponents = require("../SharedStyledComponents");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
const Elevation = 8;
const AddressAutocomplete = props => {
  const {
    searchOptions,
    isEditable,
    tooltipText,
    searchedFields
  } = props;
  const {
    formatMessage
  } = (0, _hooks.useTranslations)();
  const form = (0, _reactHookForm.useFormContext)();
  const {
    getTestId
  } = (0, _testidGenerator.default)();
  const globalApi = (0, _globalApiClientReact.useApiClient)();
  const {
    notificationData
  } = (0, _contexts.useNotificationData)();
  const {
    setNotificationData
  } = notificationData;
  const values = form.watch();
  const errorKeys = Object.keys(form.formState.errors.address ?? {});
  const isErrors = searchedFields?.some(sf => errorKeys.includes(sf));
  const [value, setValue] = (0, _react.useState)('');
  const [suggestions, setSuggestions] = (0, _react.useState)([]);
  const [isOpenSelect, setOpen] = (0, _react.useState)(false);
  const [isItemClicked, setItemClicked] = (0, _react.useState)(false);
  const [lastSearchedText, setLastSearchedText] = (0, _react.useState)('');
  const countryValue = values.address?.country;
  let minSearchLength = 3;
  const defaultMaxResults = 10;
  if (props.minSearchLength) {
    minSearchLength = props.minSearchLength;
  }
  const handleSearchTextChange = event => {
    setValue(event.target.value);
  };
  const search = async options => {
    const {
      searchText,
      top,
      country,
      pathKey
    } = options;
    const emptySuggestionsResponse = {
      addressSuggestionsCollection: []
    };
    if (!searchText) {
      return _models.FetchResult.errorFor(emptySuggestionsResponse);
    }
    const apiParams = {
      SearchText: searchText,
      Top: top,
      Country: country,
      PathKey: pathKey
    };
    try {
      const nodes = await (0, _globalApiClientReact.getAddressSuggestions)(globalApi, apiParams);
      return new _models.FetchResult(nodes.status, nodes.data);
    } catch (error) {
      setNotificationData({
        isOpen: true,
        message: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          defaultMessage: _constants.defaultTranslations.cannotGetData,
          id: _models.TranslationKey.cannotGetData
        }),
        type: _interfaces.MessageType.error
      });
    }
    return _models.FetchResult.errorFor(emptySuggestionsResponse);
  };
  const getAddress = async (nodeId, country) => {
    const emptyAddressResponse = {
      type: 'Area'
    };
    try {
      const addressNode = await (0, _globalApiClientReact.getAddressSearchResult)(globalApi, nodeId, {
        country
      });
      return new _models.FetchResult(addressNode.status, addressNode.data);
    } catch (error) {
      setNotificationData({
        isOpen: true,
        message: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          defaultMessage: _constants.defaultTranslations.cannotGetData,
          id: _models.TranslationKey.cannotGetData
        }),
        type: _interfaces.MessageType.error
      });
    }
    return _models.FetchResult.errorFor(emptyAddressResponse);
  };
  const getStateAddressSuggestions = async (searchText, pathKey) => {
    if (searchText.length >= minSearchLength) {
      const addressNodesResult = await search({
        searchText,
        top: searchOptions?.maxResults ?? defaultMaxResults,
        country: countryValue,
        pathKey
      });
      const {
        response
      } = addressNodesResult;
      if (response?.addressSuggestionsCollection) {
        const addressSuggestions = response.addressSuggestionsCollection.map(node => ({
          addressSummary: node.addressSummary,
          description: node.description,
          count: node.count,
          pathKey: node.pathKey,
          addressNodeId: node.addressNodeId
        }));
        return addressSuggestions;
      }
      return null;
    }
    return [];
  };
  const handleOnClick = async () => {
    if (value !== '' && !isOpenSelect) {
      const suggestedAddresses = await getStateAddressSuggestions(value);
      if (suggestedAddresses != null) {
        setSuggestions(suggestedAddresses);
        setOpen(true);
      }
    }
  };
  const renderInput = inputProps => {
    const {
      InputProps,
      ref,
      ...other
    } = inputProps;
    const componentProps = {
      inputRef: ref,
      ...InputProps,
      autoComplete: 'off'
    };
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.TextField, {
      InputProps: componentProps,
      ...other,
      // eslint-disable-next-line react/jsx-no-duplicate-props --- we needed is not duplicate props
      inputProps: {
        'data-testid': getTestId('autocomplete-search-field'),
        ...other.inputProps
      },
      disabled: !isEditable,
      variant: "outlined",
      onChange: handleSearchTextChange,
      onClick: handleOnClick
    });
  };
  const renderAddressSuggestionItem = suggestion => {
    const addressSummary = `${suggestion.addressSummary} `;
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontWeight: 'bold'
        },
        children: suggestion.description
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("br", {}), addressSummary, suggestion.count !== 1 && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
        defaultMessage: _constants.defaultTranslations.addressResults,
        id: _models.TranslationKey.addressResults,
        values: {
          count: suggestion.count
        }
      })]
    });
  };
  const renderSuggestion = suggestionProps => {
    const {
      suggestion,
      itemProps
    } = suggestionProps;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.MenuItem, {
      ...itemProps,
      component: "div",
      style: {
        fontWeight: 'normal',
        whiteSpace: 'unset',
        wordBreak: 'break-word'
      },
      children: renderAddressSuggestionItem(suggestion)
    });
  };
  const renderSuggestions = options => {
    const {
      getItemProps
    } = options;
    let items = [/*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.MenuItem, {
      component: "div",
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
        defaultMessage: _constants.defaultTranslations.noSuggestions,
        id: _models.TranslationKey.noSuggestions
      })
    }, 0)];
    if (suggestions.length > 0) {
      items = suggestions.map((suggestion, index) => {
        const suggestionProps = {
          index,
          suggestion,
          itemProps: getItemProps({
            item: suggestion
          })
        };
        return renderSuggestion(suggestionProps);
      });
    }
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Paper, {
      style: {
        position: 'absolute',
        zIndex: 10,
        top: 'calc(100% - 10px)',
        right: 0,
        left: 0,
        padding: '8px 0',
        overflow: 'hidden',
        background: 'white',
        maxHeight: '300px',
        overflowY: 'auto',
        textAlign: 'justify'
      },
      elevation: Elevation,
      children: items
    });
  };
  const mapNodeToAddress = node => {
    const result = values.address;
    Object.keys(node).filter(nodeKey => node[nodeKey]).forEach(nodeKey => {
      const addressNodeKey = nodeKey;
      const addressValue = node[addressNodeKey];
      if (addressNodeKey === 'street') {
        result.streetAddress = addressValue;
      } else if (addressNodeKey === 'zipCode') {
        result.zipcode = addressValue;
      } else if (addressNodeKey !== 'type' && addressNodeKey !== 'buildingDetails') {
        result[addressNodeKey] = addressValue;
      }
    });
    return result;
  };
  const handleAutocompleteKeyUp = async event => {
    const searchInput = event.target;
    if (searchInput.value === '') {
      setSuggestions([]);
      setValue('');
      setOpen(false);
    }
    const searchText = searchInput.value.trimStart();
    setValue(searchText);
    setLastSearchedText(searchText);
    const suggestedAddresses = await getStateAddressSuggestions(searchText);
    if (suggestedAddresses != null && searchText !== lastSearchedText) {
      setSuggestions(suggestedAddresses);
      setOpen(true);
    }
  };
  const getAutocompleteContent = options => {
    const {
      getInputProps,
      isOpen
    } = options;
    const {
      onBlur,
      onFocus,
      ...inputProps
    } = getInputProps({
      onKeyUp: handleAutocompleteKeyUp,
      placeholder: formatMessage(_models.TranslationKey.searchForAnAddress)
    });
    const mergedInputProps = {
      InputProps: {
        onBlur,
        onFocus,
        startAdornment: /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.InputAdornment, {
          position: "start",
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.Search, {})
        }),
        endAdornment: tooltipText ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Tooltip, {
          title: tooltipText,
          enterTouchDelay: 0,
          leaveTouchDelay: 5000,
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_SharedStyledComponents.StyledHelpCircle, {})
        }) : undefined
      },
      fullWidth: true,
      inputProps: {
        ...inputProps,
        autoComplete: 'off',
        value
      },
      error: isErrors,
      required: true,
      helperText: isErrors ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
        defaultMessage: _constants.defaultTranslations.addressSearchValidation,
        id: _models.TranslationKey.addressSearchValidation
      }) : null
    };
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        position: 'relative'
      },
      children: [renderInput(mergedInputProps), isOpen ? renderSuggestions(options) : null]
    });
  };
  const handleAddressSuggestionItemSelect = async item => {
    if (isItemClicked) {
      return;
    }
    try {
      setItemClicked(true);
      if (item && item.pathKey == null) {
        if (item.addressNodeId) {
          const address = await getAddress(item.addressNodeId, countryValue);
          setValue('');
          setSuggestions([]);
          setOpen(false);
          if (address.response) {
            const correctAddress = Object.entries(mapNodeToAddress(address.response));
            correctAddress.forEach(entry => {
              const [name, inputValue] = entry;
              form.setValue(`address.${name}`, inputValue, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: searchedFields?.includes(name)
              });
            });
          }
        }
      } else {
        const suggestedAddresses = await getStateAddressSuggestions(value, item?.pathKey);
        if (suggestedAddresses != null) {
          setSuggestions(suggestedAddresses);
          setOpen(true);
        }
      }
    } finally {
      setItemClicked(false);
    }
  };
  const handleDownshiftOuterClick = () => {
    setOpen(false);
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_downshift.default, {
    defaultHighlightedIndex: null,
    selectedItem: null,
    isOpen: isOpenSelect,
    onSelect: handleAddressSuggestionItemSelect,
    onOuterClick: handleDownshiftOuterClick,
    children: options => getAutocompleteContent(options)
  });
};
exports.AddressAutocomplete = AddressAutocomplete;
//# sourceMappingURL=AddressAutocomplete.js.map